import { Sparkles, AlertTriangle, ArrowRight, type LucideIcon } from 'lucide-react';
import { Button } from './Button';
import type { Role } from '@/types/api';

interface Props {
  role: Role;
  onClose: () => void;
}

interface Note {
  sev: 'critical' | 'high' | 'medium';
  text: string;
  cta: string;
}

const NOTES_BY_ROLE: Record<Role, Note[]> = {
  curator: [
    { sev: 'critical', text: 'Замечание №9 (критическое) открыто 25 дн., нет ответа от ГИП. Требуется эскалация.', cta: 'Эскалировать' },
    { sev: 'high',     text: '3 пробела комплектности в разделе ПОС требуют пополнения до конца недели', cta: 'Открыть ПОС' },
    { sev: 'high',     text: 'Раздел ТКР редакции №3 содержит 2 предупреждения парсинга — проверьте полноту', cta: 'Проверить' },
    { sev: 'medium',   text: 'Срок ответа по замечанию №6 истекает завтра', cta: 'Открыть №6' },
  ],
  expert: [
    { sev: 'critical', text: 'У вас 2 замечания со статусом «уточнить» — ГИП ответил, требуется проверка', cta: 'К моей очереди' },
    { sev: 'high',     text: 'Конфликт по разделу ТКР: ваше замечание №2 противоречит принятому решению по №6', cta: 'Открыть конфликт' },
  ],
  gip: [
    { sev: 'critical', text: 'Замечание №9 — расчёт уклона на ПК 142+50: срок ответа истёк 4 дн. назад', cta: 'Подготовить ответ' },
    { sev: 'high',     text: 'По замечанию №6 нужно приложить расчётное обоснование радиуса R=155м. Шаблон в RAG', cta: 'Использовать шаблон' },
    { sev: 'medium',   text: 'Технологические карты ПОС v2 — добавить расчёты трудоёмкости (МДС 12-81.2007 разд. 5)', cta: 'Открыть раздел' },
  ],
  admin: [
    { sev: 'critical', text: 'Завершить пилот UI v2 для 5 проектов', cta: 'Открыть проекты' },
    { sev: 'high',     text: 'Переиндексация RAG занимает > 1ч — проверить логи', cta: 'Открыть аудит' },
  ],
  customer: [
    { sev: 'high',   text: 'Комплектность ПД по ПП-87: незакрытые подпункты требуют пополнения до передачи в экспертизу', cta: 'Открыть Block A' },
    { sev: 'medium', text: 'Появилась новая редакция — сверьте изменения перед приёмкой', cta: 'Сравнение редакций' },
  ],
};

const SEV_COLOR: Record<Note['sev'], string> = {
  critical: '#EF4444', high: '#F59E0B', medium: '#6B7280',
};

const ROLE_LABEL: Record<Role, string> = {
  curator: 'куратор', expert: 'эксперт', gip: 'ГИП', admin: 'админ', customer: 'заказчик',
};

/** Full-screen swap-режим: замещает обычный dashboard сгенерированной ИИ-сводкой. */
export function AIFullSwapView({ role, onClose }: Props) {
  const notes = NOTES_BY_ROLE[role];
  return (
    <main className="mx-auto" style={{ maxWidth: 1280, paddingLeft: 24, paddingRight: 24, paddingTop: 32, paddingBottom: 64 }}>
      <div className="rounded-card flex items-start" style={{
        padding: '24px 32px', gap: 16,
        background: 'rgba(245,158,11,.05)', border: '1px solid rgba(245,158,11,.3)',
      }}>
        <Sparkles size={32} stroke="#F59E0B" />
        <div style={{ flex: 1 }}>
          <h2 className="font-heading text-ink" style={{ fontSize: 24, fontWeight: 600, letterSpacing: '-0.01em' }}>
            ИИ-вид для роли «{ROLE_LABEL[role]}»
          </h2>
          <p className="text-muted mt-1" style={{ fontSize: 14 }}>
            Дашборд заменён на сгенерированную ИИ-сводку с приоритезированными метриками и предложенными действиями. Модель GPT-4o · 2 минуты назад.
          </p>
        </div>
        <Button variant="secondary" size="sm" icon={<ArrowRight size={14} style={{ transform: 'rotate(180deg)' }} />} onClick={onClose}>
          Обычный вид
        </Button>
      </div>

      <div className="grid grid-cols-12" style={{ marginTop: 24, gap: 24 }}>
        <section className="col-span-8">
          <div className="rounded-card bg-white border border-border" style={{ padding: 24 }}>
            <h3 className="font-heading text-ink" style={{ fontSize: 18, fontWeight: 600 }}>На что обратить внимание сегодня</h3>
            <div className="mt-4 flex flex-col" style={{ gap: 14 }}>
              {notes.map((n, i) => <NoteItem key={i} note={n} />)}
            </div>
          </div>
        </section>

        <aside className="col-span-4 flex flex-col" style={{ gap: 16 }}>
          <MetricsCard />
          <RecsCard />
        </aside>
      </div>
    </main>
  );
}

function NoteItem({ note }: { note: Note }) {
  const color = SEV_COLOR[note.sev];
  return (
    <div
      className="flex items-start rounded-card"
      style={{
        padding: 14, gap: 12,
        background: '#F3F4F6',
        borderLeft: `4px solid ${color}`,
      }}
    >
      <AlertTriangle size={18} stroke={color} className="shrink-0 mt-0.5" />
      <div style={{ flex: 1 }}>
        <div className="text-ink" style={{ fontSize: 14, lineHeight: 1.55 }}>{note.text}</div>
        <button
          type="button"
          disabled
          title="Действия из ИИ-сводки появятся в следующих версиях"
          className="rounded-btn flex items-center mt-2"
          style={{ padding: '6px 12px', gap: 6, fontSize: 12, fontWeight: 500, background: '#FFFFFF', border: '1px solid #E5E7EB', cursor: 'not-allowed', opacity: 0.6 }}
        >
          {note.cta} <ArrowRight size={12} />
        </button>
      </div>
    </div>
  );
}

interface Metric { label: string; value: string; sub: string; icon?: LucideIcon }
const METRICS: Metric[] = [
  { label: 'Открытых замечаний', value: '3',     sub: '−1 за сегодня' },
  { label: 'Среднее время ответа', value: '3.2 дн.', sub: 'было 4.1 дн.' },
  { label: 'Прогресс редакции',  value: '62%',   sub: '+8% за неделю' },
];

function MetricsCard() {
  return (
    <div className="rounded-card bg-white border border-border" style={{ padding: 20 }}>
      <h3 className="font-heading text-ink" style={{ fontSize: 16, fontWeight: 600 }}>Метрики</h3>
      <div className="mt-3.5 flex flex-col" style={{ gap: 12 }}>
        {METRICS.map((m, i) => (
          <div key={i} style={{ paddingBottom: 12, borderBottom: i < METRICS.length - 1 ? '1px solid #E5E7EB' : 'none' }}>
            <div className="text-muted uppercase tracking-wider" style={{ fontSize: 11, fontWeight: 600 }}>{m.label}</div>
            <div className="font-heading tabular text-ink mt-1" style={{ fontSize: 24, fontWeight: 600 }}>{m.value}</div>
            <div className="mt-0.5" style={{ fontSize: 11, color: '#10B981' }}>{m.sub}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

const RECS = [
  'Запросить сортировку замечаний — последний был 7 дн. назад',
  'Перераспределить открытые замечания — у А.С. Морозова перегруз',
  'Обновить паспорт объекта — последний раз 3 нед. назад',
];

function RecsCard() {
  return (
    <div className="rounded-card bg-white border border-border" style={{ padding: 20 }}>
      <h3 className="font-heading text-ink" style={{ fontSize: 16, fontWeight: 600 }}>Рекомендации</h3>
      <ul className="mt-3 flex flex-col" style={{ paddingLeft: 18, gap: 8 }}>
        {RECS.map((r, i) => <li key={i} className="text-ink" style={{ fontSize: 13, lineHeight: 1.5 }}>{r}</li>)}
      </ul>
    </div>
  );
}
